class Wishlist {
  constructor() {
    this.storageKey = 'noopy_wishlist';
    this.customerId = window.__wishlistCustomerId || null;
    this.cloudSynced = false;

    /* Initialiser en local d'abord, puis sync Firebase quand prêt */
    this.init();

    if (this.customerId) {
      /* Client connecté : attendre Firebase puis synchroniser */
      if (window.__wishlistFirestore) {
        this._syncWithCloud();
      } else {
        window.addEventListener('firebase:ready', () => this._syncWithCloud(), { once: true });
      }
    }

    this._observeDom();

    /* Garder les onglets synchronisés entre eux */
    window.addEventListener('storage', (e) => {
      if (e.key === this.storageKey) {
        this.init();
        this._notify();
      }
    });
  }

  /* ─────────────────────────────────────────
     LOCALSTORAGE (toujours utilisé en local)
  ───────────────────────────────────────── */

  getWishlist() {
    try {
      const data = localStorage.getItem(this.storageKey);
      const parsed = data ? JSON.parse(data) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      return [];
    }
  }

  saveWishlist(list) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(list));
    } catch (err) {
      console.error('[Wishlist] Erreur localStorage:', err);
    }
    this.updateHeaderCount();
    /* Sync cloud si client connecté */
    if (this.customerId && this.cloudSynced) {
      this._saveToCloud(list);
    }
  }

  /* ─────────────────────────────────────────
     FIREBASE (cloud)
  ───────────────────────────────────────── */

  async _syncWithCloud() {
    try {
      const { db, doc, getDoc, setDoc } = window.__wishlistFirestore;
      const ref = doc(db, 'wishlists', this.customerId);
      const snap = await getDoc(ref);

      const localList = this.getWishlist();
      const cloudList = snap.exists() ? (snap.data().items || []) : [];

      /* Fusion : local + cloud sans doublons (clé = id produit) */
      const merged = this._mergeLists(localList, cloudList);

      /* Sauvegarder la liste fusionnée partout */
      localStorage.setItem(this.storageKey, JSON.stringify(merged));
      await setDoc(ref, { items: merged, updatedAt: new Date().toISOString() });

      this.cloudSynced = true;
      this.init(); /* Rafraîchir les cœurs avec la liste fusionnée */
      this._notify(); /* Prévenir la page wishlist si elle est affichée */
    } catch (err) {
      console.error('[Wishlist] Erreur sync Firebase:', err);
      this.cloudSynced = false;
    }
  }

  async _saveToCloud(list) {
    try {
      const { db, doc, setDoc } = window.__wishlistFirestore;
      const ref = doc(db, 'wishlists', this.customerId);
      await setDoc(ref, { items: list, updatedAt: new Date().toISOString() });
    } catch (err) {
      console.error('[Wishlist] Erreur sauvegarde Firebase:', err);
    }
  }

  /* Fusion de deux listes sans doublons (le local écrase le cloud si même ID) */
  _mergeLists(listA, listB) {
    const map = new Map();
    [...listB, ...listA].forEach(item => {
      if (item && item.id) map.set(String(item.id), item);
    });
    return Array.from(map.values());
  }

  /* Prévient les autres scripts (ex : la grille de la page wishlist)
     que la liste a changé en dehors d'un clic local */
  _notify() {
    window.dispatchEvent(new CustomEvent('wishlist:sync'));
  }

  /* ─────────────────────────────────────────
     OPÉRATIONS PRODUIT
  ───────────────────────────────────────── */

  addProduct(productId, productTitle, productImage, productUrl, productPrice) {
    const list = this.getWishlist();
    if (!list.some(item => String(item.id) === String(productId))) {
      let fixedImage = productImage;
      if (fixedImage && fixedImage.startsWith('//')) {
        fixedImage = 'https:' + fixedImage;
      }
      list.push({
        id: String(productId),
        title: productTitle,
        image: fixedImage,
        url: productUrl,
        price: productPrice,
        addedAt: new Date().toISOString()
      });
      this.saveWishlist(list);
      this.updateAllHearts();
      return true;
    }
    return false;
  }

  removeProduct(productId) {
    const list = this.getWishlist().filter(item => String(item.id) !== String(productId));
    this.saveWishlist(list);
    this.updateAllHearts();
  }

  isInWishlist(productId) {
    return this.getWishlist().some(item => String(item.id) === String(productId));
  }

  toggleProduct(productId, productTitle, productImage, productUrl, productPrice) {
    if (this.isInWishlist(productId)) {
      this.removeProduct(productId);
      return false;
    }
    this.addProduct(productId, productTitle, productImage, productUrl, productPrice);
    return true;
  }

  getCount() {
    return this.getWishlist().length;
  }

  /* ─────────────────────────────────────────
     UI
  ───────────────────────────────────────── */

  init() {
    this.updateAllHearts();
    this.updateHeaderCount();
  }

  updateAllHearts() {
    /* La liste n'est lue qu'une seule fois, pas une fois par bouton */
    const ids = new Set(this.getWishlist().map(item => String(item.id)));

    document.querySelectorAll('[data-wishlist-button]').forEach(button => {
      const active = ids.has(String(button.dataset.productId));

      button.classList.toggle('active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');

      /* Labels traduits, fournis par le snippet wishlist-button.liquid */
      const label = active ? button.dataset.labelRemove : button.dataset.labelAdd;
      if (label) {
        button.setAttribute('aria-label', label);
        button.setAttribute('title', label);
      }
    });
  }

  updateHeaderCount() {
    const count = this.getWishlist().length;
    const counter = document.querySelector('[data-wishlist-count]');
    const counterMobile = document.querySelector('[data-wishlist-count-mobile]');
    const headerIcon = document.querySelector('.header__icon--wishlist');

    if (counter) {
      counter.textContent = count;
      counter.style.display = count > 0 ? 'flex' : 'none';
    }
    if (counterMobile) {
      counterMobile.textContent = count;
      counterMobile.style.display = count > 0 ? 'flex' : 'none';
    }
    if (headerIcon) {
      headerIcon.classList.toggle('has-items', count > 0);
    }
  }

  /* Vessel remplace/morphe des morceaux de page (filtres, tri, quick add,
     hydratation du header...). On resynchronise l'état dès que :
     - de nouveaux cœurs ou un nouveau compteur apparaissent
     - le serveur réécrit la classe d'un cœur existant */
  _observeDom() {
    let scheduled = false;

    const isRelevantNode = (node) =>
      node.nodeType === 1 &&
      (node.hasAttribute('data-wishlist-button') ||
        node.hasAttribute('data-wishlist-count') ||
        node.querySelector('[data-wishlist-button], [data-wishlist-count]'));

    const observer = new MutationObserver((mutations) => {
      if (scheduled) return;

      const relevant = mutations.some(m =>
        m.type === 'attributes'
          ? m.target.hasAttribute('data-wishlist-button')
          : Array.from(m.addedNodes).some(isRelevantNode)
      );
      if (!relevant) return;

      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        this.init();
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });
  }
}

/* ─────────────────────────────────────────
   INITIALISATION
───────────────────────────────────────── */
const wishlist = new Wishlist();
window.wishlist = wishlist;

/* Clics sur les boutons cœur.
   Écoute en phase de capture : les cartes produit de Vessel ont leur propre
   gestion de clic (view transition vers la fiche produit), on l'empêche. */
document.addEventListener('click', (e) => {
  const button = e.target.closest('[data-wishlist-button]');
  if (!button) return;
  e.preventDefault();
  e.stopPropagation();

  wishlist.toggleProduct(
    button.dataset.productId,
    button.dataset.productTitle,
    button.dataset.productImage,
    button.dataset.productUrl,
    button.dataset.productPrice
  );
}, true);
